/* istanbul ignore file */

import React, { memo } from 'react';
import { StyleSheet, View } from 'react-native';

import { useTrackEventEffect } from '@order/Telemetry';

import { useDeliveryOrderInFlight } from '../../hooks';
import {
  GroupOrderLeaveConfirmationModal,
  GroupOrderReorderConfirmationModal,
} from '../../screens/ReorderingScreen/components';
import { LocationWarningModal } from '../LocationWarningModal';
import { CompleteAddressFormModal, LocationSearchContent } from './components';
import { getAddressForm } from './helpers';
import { useLocationSearchMachine } from './hooks';

import type { LocationSearchMachineContext } from './machine';

// ─────────────────────────────────────────────────────────────────────────────

export const LocationSearch = memo((props: LocationSearchProps) => {
  const {
    isInsideModal,
    interactedLocationRestaurantSlug,
    interactedLocationDeliveryAddressId,
    navigateToLocation,
    navigateToDeliveryLocation,
    withNavHeader,
    onNavClose,
    shouldRequestLocationPermission,
  } = props;

  useDeliveryOrderInFlight();

  const { state, service, flags, helpers } = useLocationSearchMachine({
    isInsideModal,
    navigateToLocation,
    navigateToDeliveryLocation,
    shouldRequestLocationPermission,
  });

  // ─── Derived ─────────────────────────────────────────────────────────

  const { deliveryLocation, activeLocation } = state.context;

  const { isAddingAddress, shouldShowLocationWarning, shouldRenderDeliveryAddressForm } = flags;

  const {
    confirmLocationWarning,
    cancelLocationWarning,
    cancelDeliveryAddressForm,
    submitDeliveryAddressForm,
  } = helpers;

  // ─── Effects ─────────────────────────────────────────────────────────

  useTrackEventEffect({ name: 'location.search.view' });

  // ─────────────────────────────────────────────────────────────────────

  return (
    <>
      <LocationSearchContent
        locationSearchActorRef={service}
        interactedLocationRestaurantSlug={interactedLocationRestaurantSlug}
        interactedLocationDeliveryAddressId={interactedLocationDeliveryAddressId}
        isInsideModal={isInsideModal}
        withNavHeader={withNavHeader}
        onNavClose={onNavClose}
      />

      {deliveryLocation ? (
        <CompleteAddressFormModal
          address={getAddressForm(deliveryLocation)}
          isVisible={shouldRenderDeliveryAddressForm}
          isAddingAddress={isAddingAddress}
          onRequestClose={cancelDeliveryAddressForm}
          onSubmit={submitDeliveryAddressForm}
        />
      ) : null}

      {shouldShowLocationWarning && activeLocation ? (
        <View style={StyleSheet.absoluteFill}>
          <LocationWarningModal
            locations={[activeLocation]}
            onClose={cancelLocationWarning}
            onConfirm={confirmLocationWarning}
            restaurantSlug={activeLocation.restaurantSlug}
          />
        </View>
      ) : null}

      <GroupOrderReorderConfirmationModal />

      <GroupOrderLeaveConfirmationModal isInsideModal={isInsideModal} />
    </>
  );
});

// ─── Types ───────────────────────────────────────────────────────────────────

type LocationSearchProps = {
  isInsideModal?: boolean;

  /**
   * While interacting with a location card (reordering, navigation, etc.),
   * we occasionally need to display the loading, disabled states.
   * To display the corresponding additional state, we match the target location
   * card restaurant slug to the interacted location restaurant slug and/or delivery address ID.
   * */
  interactedLocationRestaurantSlug: string | undefined;
  interactedLocationDeliveryAddressId: string | undefined;

  /*
    Optional actions that initiates the navigation process for the selected location.
    When those properties are not provided, the standard navigation is used.

    Helpful when a custom navigation method is utilized or when other actions
    need to be performed before navigation.
   */

  navigateToLocation?: (context: LocationSearchMachineContext) => void;
  navigateToDeliveryLocation?: (context: LocationSearchMachineContext) => void;

  withNavHeader?: boolean;
  onNavClose?: () => void;

  /**
   * When `false`, the search machine skips the setup phase that would
   * otherwise request the OS location permission. Useful when an upstream
   * consumer (e.g. the location permission primer) owns that flow.
   */
  shouldRequestLocationPermission?: boolean;
};
